import { Component, OnInit, NgZone, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';

declare const google: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  loginForm: FormGroup;
  registerForm: FormGroup;
  isLoginMode = true;
  loading = false;
  googleLoading = false;
  errorMessage = '';
  showPassword = false;
  showConfirmPassword = false;
  googleReady = false;

  // Convite
  inviteToken: string | null = null;
  inviteInfo: any = null;
  validatingInvite = false;

  // Indicação (Referral)
  referralToken: string | null = null;
  validatingReferral = false;

  // ⚠️ SUBSTITUA pelo seu Client ID do Google Cloud Console
  // https://console.cloud.google.com → APIs e Serviços → Credenciais
  private readonly GOOGLE_CLIENT_ID = '689495053471-u7ir4r2cq4otjrdl9hq42noif4s39ar3.apps.googleusercontent.com';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private api: ApiService
  ) {
    this.loginForm = this.fb.group({
      email:    ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.registerForm = this.fb.group({
      name:            ['', Validators.required],
      email:           ['', [Validators.required, Validators.email]],
      phone:           ['', Validators.required],
      password:        ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.waitForGoogleAndInit();

    // Se acessou via /register, força modo de registro
    if (this.route.snapshot.routeConfig?.path === 'register') {
      this.isLoginMode = false;
    }

    // Verificar se há token de convite na URL
    this.route.queryParams.subscribe(params => {
      const token = params['invite'];
      if (token) {
        this.inviteToken = token;
        this.isLoginMode = false; // Força modo de registro
        this.validateInviteToken(token);
      }

      // Verificar se há token de indicação na URL
      const refToken = params['ref'];
      if (refToken) {
        this.isLoginMode = false; // Força modo de registro
        this.validateReferralToken(refToken);
      }
    });
  }

  ngAfterViewInit(): void {
    // Segunda tentativa após view renderizada
    setTimeout(() => this.renderGoogleButton(), 500);
  }

  // ===== GOOGLE: aguarda SDK carregar =====
  private waitForGoogleAndInit(attempts = 0): void {
    if (typeof google !== 'undefined' && google?.accounts?.id) {
      this.initGoogle();
    } else if (attempts < 20) {
      // Tenta a cada 200ms por até 4 segundos
      setTimeout(() => this.waitForGoogleAndInit(attempts + 1), 200);
    } else {
      console.warn('Google SDK não carregou após 4s. Verifique a conexão.');
    }
  }

  private initGoogle(): void {
    try {
      google.accounts.id.initialize({
        client_id:             this.GOOGLE_CLIENT_ID,
        callback:              (res: any) => this.handleCredential(res),
        auto_select:           false,
        cancel_on_tap_outside: true,
        ux_mode:               'popup',
        locale:                'pt-BR',
        use_fedcm_for_prompt: true,
      });
      this.googleReady = true;
      this.renderGoogleButton();
    } catch (e) {
      console.error('Erro ao inicializar Google Sign-In:', e);
    }
  }

  private renderGoogleButton(): void {
    if (!this.googleReady) return;

    const container = document.getElementById('google-btn-container');
    if (container && container.childElementCount === 0) {
      try {
        google.accounts.id.renderButton(container, {
          theme:          'outline',
          size:           'large',
          width:          container.offsetWidth || 340,
          text:           'signin_with',
          logo_alignment: 'left',
          locale:         'pt-BR',
          shape:          'rectangular'
        });
      } catch (e) {
        console.error('Erro ao renderizar botão Google:', e);
      }
    }
  }

  // Callback chamado pelo Google após autorização
  handleCredential(response: any): void {
    this.ngZone.run(() => {
      if (!response?.credential) {
        this.errorMessage = 'Falha na autenticação com Google. Tente novamente.';
        return;
      }

      this.googleLoading = true;
      this.errorMessage  = '';

      this.authService.googleLogin(response.credential).subscribe({
        next: (res: any) => {
          this.googleLoading = false;
          if (res.success) {
            this.router.navigate(['/dashboard']);
          } else {
            this.errorMessage = res.message || 'Erro ao autenticar com Google';
          }
        },
        error: (err: any) => {
          this.googleLoading = false;
          this.errorMessage  = err.error?.message || 'Erro ao autenticar com Google';
        }
      });
    });
  }

  // Botão customizado — abre popup de seleção de conta
  onGoogleClick(): void {
    if (this.googleReady && typeof google !== 'undefined') {
      try {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Popup bloqueado → tenta One Tap
            this.errorMessage = 'Popup bloqueado pelo navegador. Permita popups para este site.';
          }
        });
      } catch (e) {
        this.errorMessage = 'Erro ao abrir login Google. Tente recarregar a página.';
      }
    } else {
      // SDK ainda não carregou — tenta carregar dinamicamente
      this.loadGoogleScriptDynamically();
    }
  }

  // Carrega o script dinamicamente como último recurso
  private loadGoogleScriptDynamically(): void {
    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      this.waitForGoogleAndInit();
      return;
    }

    const script    = document.createElement('script');
    script.id       = 'google-gsi-script';
    script.src      = 'https://accounts.google.com/gsi/client';
    script.onload   = () => this.waitForGoogleAndInit();
    script.onerror  = () => {
      this.errorMessage = 'Não foi possível carregar o Google Sign-In. Verifique sua conexão.';
    };
    document.head.appendChild(script);
  }

  // ===== LOGIN =====
  onLogin(): void {
    if (this.loginForm.invalid) { this.loginForm.markAllAsTouched(); return; }
    this.loading = true;
    this.errorMessage = '';

    this.authService.login(this.loginForm.value).subscribe({
      next: (res: any) => {
        this.loading = false;
        res.success
          ? this.router.navigate(['/dashboard'])
          : (this.errorMessage = res.message || 'Email ou senha incorretos');
      },
      error: (err: any) => {
        this.loading      = false;
        this.errorMessage = err.error?.message || 'Email ou senha incorretos';
      }
    });
  }

  // ===== REGISTRO =====
  onRegister(): void {
    if (this.registerForm.invalid) { this.registerForm.markAllAsTouched(); return; }
    this.loading = true;
    this.errorMessage = '';

    const { confirmPassword, ...data } = this.registerForm.value;

    // Se há convite, usa endpoint específico
    if (this.inviteToken) {
      this.api.registerWithInvite(this.inviteToken, data.name, data.phone, data.password).subscribe({
        next: (res: any) => {
          this.loading = false;
          res.success
            ? this.router.navigate(['/dashboard'])
            : (this.errorMessage = res.message || 'Erro ao criar conta');
        },
        error: (err: any) => {
          this.loading      = false;
          this.errorMessage = err.error?.message || 'Erro ao criar conta com convite';
        }
      });
    } else {
      // Registro normal (pode incluir referral token)
      const referralToken = sessionStorage.getItem('referralToken');
      const payload = referralToken ? { ...data, referralToken } : data;

      this.authService.register(payload).subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res.success) {
            sessionStorage.removeItem('referralToken'); // Limpa token após uso
            this.router.navigate(['/dashboard']);
          } else {
            this.errorMessage = res.message || 'Erro ao criar conta';
          }
        },
        error: (err: any) => {
          this.loading      = false;
          this.errorMessage = err.error?.message || 'Erro ao criar conta';
        }
      });
    }
  }

  // ===== CONVITE =====
  validateInviteToken(token: string): void {
    this.validatingInvite = true;
    this.api.validateInvite(token).subscribe({
      next: (res: any) => {
        this.validatingInvite = false;
        if (res.isValid) {
          this.inviteInfo = res;
          // Pré-preenche o email e bloqueia
          this.registerForm.patchValue({ email: res.email });
          this.registerForm.get('email')?.disable();
        } else {
          this.errorMessage = res.message || 'Convite inválido ou expirado';
          this.inviteToken = null;
        }
      },
      error: () => {
        this.validatingInvite = false;
        this.errorMessage = 'Erro ao validar convite';
        this.inviteToken = null;
      }
    });
  }

  validateReferralToken(token: string): void {
    this.validatingReferral = true;
    this.api.validateReferralToken(token).subscribe({
      next: (res: any) => {
        this.validatingReferral = false;
        if (res.valid) {
          this.referralToken = token;
          // Pré-preenche o email se vier da indicação
          if (res.referredEmail) {
            this.registerForm.patchValue({ email: res.referredEmail });
          }
          // Armazena o token no sessionStorage para uso posterior
          sessionStorage.setItem('referralToken', token);
        } else {
          this.errorMessage = res.message || 'Link de indicação inválido ou expirado';
          this.referralToken = null;
        }
      },
      error: () => {
        this.validatingReferral = false;
        this.errorMessage = 'Erro ao validar link de indicação';
        this.referralToken = null;
      }
    });
  }

  // ===== HELPERS =====
  toggleMode(): void {
    this.isLoginMode  = !this.isLoginMode;
    this.errorMessage = '';
    this.loginForm.reset();
    this.registerForm.reset();
    // Re-renderiza o botão Google após troca de aba
    setTimeout(() => this.renderGoogleButton(), 150);
  }

  passwordMatchValidator(form: FormGroup) {
    const pw  = form.get('password')?.value;
    const cpw = form.get('confirmPassword')?.value;
    return pw && cpw && pw !== cpw ? { passwordMismatch: true } : null;
  }

  get f() { return this.loginForm.controls; }
  get r() { return this.registerForm.controls; }

  formatPhone(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length === 0) return '';
    if (v.length <= 2)  return `(${v}`;
    if (v.length <= 6)  return `(${v.substring(0, 2)}) ${v.substring(2)}`;
    if (v.length <= 10) return `(${v.substring(0, 2)}) ${v.substring(2, 6)}-${v.substring(6)}`;
    return `(${v.substring(0, 2)}) ${v.substring(2, 7)}-${v.substring(7)}`;
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatPhone(input.value);
    input.value = formatted;
    this.registerForm.get('phone')?.setValue(formatted, { emitEvent: false });
  }
}
